import {
  Button,
  FieldAddonDropdown,
  FlexBox,
  ModalBody,
  ModalFoot,
  ModalHead,
  Text,
} from '@dixa/lib-shared-habla';
import { zodResolver } from '@hookform/resolvers/zod';
import { useAtomValue } from 'jotai';
import { FieldInputText } from 'lib-knowledge-form';
import CategoryTreeInput from 'lib-knowledge-management-common/components/categoryTreeInput/index';
import { LanguageContext } from 'lib-knowledge-management-common/contexts/language';
import { CategoryCreateMutation } from 'lib-knowledge-management-common/graphql';
import { ModalWrapper, useModal } from 'lib-knowledge-modal';
import { useNotification } from 'lib-knowledge-utils/hooks/useNotification';
import { collectionIdAtom } from 'models/collectionStore';
import { useContext } from 'react';
import { Controller, useForm } from 'react-hook-form';

import { useCategoryCreate } from 'hooks/useCategoryCreate';

import {
  CategoryAddModalSchema,
  categoryAddModalSchema,
} from './categoryAddModalSchema';

export function CategoryAddModal(): JSX.Element {
  const { hideModal } = useModal();
  const { errorNotify, successNotify } = useNotification();
  const { selectedLanguage, enabledLanguages } = useContext(LanguageContext);

  const collectionId = useAtomValue(collectionIdAtom);
  if (!collectionId)
    throw new Error('collectionId not defined in CategoryAddModalContent');

  const {
    control,
    watch,
    formState: { isValid: isFormValid },
    handleSubmit,
  } = useForm<CategoryAddModalSchema>({
    resolver: zodResolver(categoryAddModalSchema),
    defaultValues: {
      categoryTitle: '',
      language: {
        id: selectedLanguage.languageId,
        name: selectedLanguage.description,
      },
      parentRef: {
        __typename: 'Collection',
        id: collectionId,
      },
    },
    reValidateMode: 'onChange',
    mode: 'all',
  });

  const parentObj = watch('parentRef');
  const catTitle = watch('categoryTitle');

  const [createCategory, { loading: creatingCategory }] = useCategoryCreate({
    onCompleted: (result: CategoryCreateMutation) => {
      hideModal();
      successNotify({
        title: 'Category Created',
        text: `"${result.categoryCreate?.titles[0]?.title}" has been created`,
      });
    },
    onError: (err) => {
      errorNotify({
        text: `"${catTitle}" could not be created`,
      });
      throw err;
    },
  });

  const selectedLang = watch('language');

  const dropdownListItems = enabledLanguages.map((l) => ({
    id: l.languageId,
    name: l.description,
  }));

  const onSubmit = ({
    categoryTitle,
    language,
    parentRef,
  }: CategoryAddModalSchema) => {
    if (!parentRef) return;
    createCategory({
      categoryTitle,
      languageId: language.id,
      parentRef,
    });
  };

  return (
    <ModalWrapper>
      <form onSubmit={handleSubmit(onSubmit)}>
        <ModalHead title="Create category" onClose={hideModal}></ModalHead>
        <ModalBody>
          <Text.Paragraph variant="2" subdued withMarginBottom>
            A category is a grouping of closely related articles on a shared
            topic. Use these to organize your collection into more manageable
            groups of content.
          </Text.Paragraph>

          <FieldInputText
            control={control}
            name="categoryTitle"
            label="Name"
            required
            placeholder="Category name..."
            prefix={{
              type: 'custom',
              noWrap: true,
              value: (
                <Controller
                  control={control}
                  name="language"
                  render={({ field }) => {
                    return (
                      <FieldAddonDropdown
                        meta={{
                          selectedValue: <span>{selectedLang.name}</span>,
                        }}
                        items={dropdownListItems}
                        onSelect={(item) => field.onChange(item)}
                      />
                    );
                  }}
                />
              ),
            }}
          />

          <Controller
            control={control}
            name="parentRef"
            render={({ field }) => {
              return (
                <CategoryTreeInput
                  withCollectionOption
                  value={parentObj}
                  onChange={(val) => {
                    field.onChange(val);
                  }}
                  currentCategoryId={undefined}
                  collectionId={collectionId}
                />
              );
            }}
          />
        </ModalBody>
        <ModalFoot>
          <FlexBox justifyContent="flex-end" gap="100">
            <Button
              disabled={creatingCategory}
              variant="tertiary"
              onClick={hideModal}
            >
              Cancel
            </Button>
            <Button disabled={creatingCategory || !isFormValid} type="submit">
              Save
            </Button>
          </FlexBox>
        </ModalFoot>
      </form>
    </ModalWrapper>
  );
}
